import {BehaviorSubject, Observable, of, Subscription} from 'rxjs';
import {Injectable}                                    from '@angular/core';
import {Create, ObjectFactory}                         from 'ts-tooling';
import {
  FwPlanningWizardStates,
  IFwPlanPlanningWizardDetailsData,
  IFwPlanPlanningWizardDetailsProvider,
  IFwPlanPlanningWizardDetailsResponsiveData,
  IFwPlanPlanningWizardProvider,
  IFwPlanPlanningWizardSelectionData,
  WorkTypeCategory
}                                                      from '../fw-plan-planning.types';
import {FwPlanPlanningDetailsConfig}                   from '../configs/fw-plan-planning-details.config';
import {
  ApDynGridColumnConfigBase
}                                                      from '../../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                      from '../../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  ApDynformsConfigFieldset
}                                                      from '../../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {ApDynformsComponent}                           from '../../../../ap-dynforms/ap-dynforms.component';
import {nameOf}                                        from '../../../../ap-core/utils/ap-name-of';
import {WorkTypesStore}                                from '../../../../stores/base-data/worrktypes.store';
import {GetRoundNumericService}                        from '../../../../ap-utils/service/get-round-numeric.service';
import {SettingsStore}                                 from '../../../../stores/base-data/settings.store';
import {WorktypeUnitStore}                             from '../../../../stores/base-data/worktype-unit.store';
import {ApDateService}                                 from '../../../../ap-core/services/ap-date-service';
import {InstrumentStore}                               from '../../../../stores/docu/instrument.store';
import {MachineStore}                                  from '../../../../stores/docu/machine.store';
import IUnit = Data.Common.IUnit;
import IWorktypes = Data.BaseData.IWorktypes;
import IGuid = System.IGuid;

@Injectable()
export class FwPlanPlanningDetailsProvider implements IFwPlanPlanningWizardDetailsProvider,
  IFwPlanPlanningWizardProvider<IFwPlanPlanningWizardSelectionData, IFwPlanPlanningWizardDetailsData> {

  private _dynForm: ApDynformsComponent;
  private _subscriptions: Subscription[] = [];
  private _responsiveData: IFwPlanPlanningWizardDetailsResponsiveData;
  private _detailsData: IFwPlanPlanningWizardDetailsData = undefined;
  private _caption$: BehaviorSubject<string>;
  private _fieldSets$: BehaviorSubject<ApDynformsConfigFieldset[]>;

  constructor(private machineStore: MachineStore,
              private settingsStore: SettingsStore,
              private workTypeStore: WorkTypesStore,
              private instrumentStore: InstrumentStore,
              private workTypeUnitStore: WorktypeUnitStore,
              private dateService: ApDateService,
              private roundNumericService: GetRoundNumericService,
              private fwPlanPlanningWizardDetailsUiProvider: FwPlanPlanningDetailsConfig) {
  }

  /**
   * Initializes the provider with required dependencies
   * @param caption$ - BehaviorSubject for the caption
   * @param fieldSets$ - BehaviorSubject for form fieldsets
   * @param dynForm - Reference to the dynamic form component
   * @public
   */
  public init(caption$: BehaviorSubject<string>,
              fieldSets$: BehaviorSubject<ApDynformsConfigFieldset[]>,
              dynForm: ApDynformsComponent): void {
    this._responsiveData = {
      WorkTypes$: new BehaviorSubject<IWorktypes[]>([]),
      WorkTypeUnits$: new BehaviorSubject<IUnit[]>([]),
      WorkTypeUnitsDisabled$: new BehaviorSubject<boolean>(true),
      WorkTypeValueDisabled$: new BehaviorSubject<boolean>(true),
      WorkTypeCategories$: new BehaviorSubject<WorkTypeCategory[]>([])
    };
    this._caption$ = caption$;
    this._fieldSets$ = fieldSets$;
    this._dynForm = dynForm;
  }

  /**
   * Builds details item based on selection data
   * @param selectionData - Array of selected field data
   * @returns Details a data object
   * @public
   */
  public buildItem(selectionData: IFwPlanPlanningWizardSelectionData[]): IFwPlanPlanningWizardDetailsData {
    const totalArea = this.roundNumericService.round(
      selectionData.reduce((sum, s) => sum + Create(s?.Area, 0), 0),
      this.settingsStore.FirstSetting.DigitsAfterDecimalPoint
    );
    this.setWorkTypeCategories();
    if (!!this._detailsData) {
      this._detailsData.CountFields = selectionData.length;
      this._detailsData.TotalArea = totalArea;
      this.setWorkTypes(this._detailsData.WorkTypeCategory);
      this.setWorkTypeUnits(this._detailsData.WorkType, this._detailsData);
    } else {
      this._detailsData = {
        CountFields: selectionData.length,
        TotalArea: totalArea,
        ApplicationDate: this.dateService.getFarmDate(),
        Notes: '',
        WorkTypeCategory: undefined,
        Unit: undefined,
        Value: undefined,
        Start: undefined,
        HaPerHour: undefined,
        WorkType: undefined,
        Machine: undefined,
        Driver: undefined,
        Instrument: undefined
      } as IFwPlanPlanningWizardDetailsData;
    }
    return this._detailsData;
  }

  /**
   * Handles form value changes
   * @param valueChangesArgs - Contains changed control name and value
   * @public
   */
  public onFormValueChanges(valueChangesArgs: { control: string; value: any }): void {
    this._detailsData = this._dynForm.form.getRawValue();
    if (valueChangesArgs.control === nameOf<IFwPlanPlanningWizardDetailsData>('WorkTypeCategory')) {
      this._detailsData.WorkType = undefined;
      this._detailsData.Value = undefined;
      this._detailsData.Unit = undefined;
      this.setWorkTypes(this._detailsData.WorkTypeCategory);
    }
    if (valueChangesArgs.control === nameOf<IFwPlanPlanningWizardDetailsData>('WorkType')) {
      this._detailsData.Value = undefined;
      this._detailsData.Unit = undefined;
      this.setWorkTypeUnits(this._detailsData.WorkType, this._detailsData);
    }
    if (valueChangesArgs.control === nameOf<IFwPlanPlanningWizardDetailsData>('Machine')) {
      const machine = this.machineStore.getMachineById(this._detailsData.Machine);
      if (!!machine) {
        if (!this._detailsData.Driver) {
          this._detailsData.Driver = machine.DefaultDriver?.Id;
        }
        if (!this._detailsData.Instrument) {
          this.setInstrumentData(machine.DefaultInstrument?.Id, this._detailsData);
        }
      }
    }
    if (valueChangesArgs.control === nameOf<IFwPlanPlanningWizardDetailsData>('Instrument')) {
      this.setInstrumentData(this._detailsData.Instrument, this._detailsData);
    }
    this.patchForm();
  }

  /**
   * Cleans up resources when a component is destroyed
   * @public
   */
  public onDestroy(): void {
    this._responsiveData = undefined;
    this._detailsData = undefined;
    this._subscriptions?.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }

  /**
   * Returns copy of details data when leaving a component
   * @public
   */
  public onLeave(): IFwPlanPlanningWizardDetailsData {
    return ObjectFactory.Copy(this._detailsData);
  }

  /**
   * Updates caption text
   * @public
   */
  public updateCaption(): void {
    this._caption$.next('Global__Details');
  }

  /**
   * Updates form fieldsets
   * @public
   */
  public updateFieldSets(): void {
    const fieldsets = this.fwPlanPlanningWizardDetailsUiProvider.generateFields(
      this._detailsData, this._responsiveData);
    this._fieldSets$.next(fieldsets);
  }

  /**
   * Handles grid selection changes
   * @public
   */
  public onGridSelectionChanged(_items: IFwPlanPlanningWizardSelectionData[]): void {
  }

  /**
   * Returns grid column configuration
   * @public
   */
  public getGridColumnConfig(_detailsData: IFwPlanPlanningWizardDetailsData): Observable<ApDynGridColumnConfigBase[]> {
    return of([]);
  }

  /**
   * Returns pager configuration
   * @public
   */
  public getPagerConfig(_breadCrumbsColumns: FwPlanningWizardStates[]): ApDynGridPagerConfigBase {
    return undefined;
  }

  /**
   * Updates form values
   * @param triggerValidation - Whether to trigger validation
   * @public
   */
  private patchForm(triggerValidation?: boolean): void {
    setTimeout(() => {
      this._dynForm.form.patchValue(this._detailsData, {emitEvent: false});
      if (triggerValidation) {
        setTimeout(() => this._dynForm.markAsTouched(), 0);
      }
    }, 0);
  }

  /**
   * Sets the instrument data in the planning wizard details.
   * @param instrumentId - The unique identifier of the instrument, can be undefined.
   * @param detailsData - The planning wizard details a data object to update with instrument-related information.
   * @return Does not return a value.
   * @private
   */
  private setInstrumentData(instrumentId: IGuid | undefined, detailsData: IFwPlanPlanningWizardDetailsData): void {
    const instrument = this.instrumentStore.getInstrumentById(instrumentId);
    detailsData.Instrument = instrument?.Id;
    if (Number.isFinite(instrument?.DefaultHaPerHour)) {
      detailsData.HaPerHour = instrument.DefaultHaPerHour;
    }
  }

  /**
   * Sets work type units based on selected work type
   * @param workTypeId - Selected work type ID
   * @param detailsData - Current details data
   * @private
   */
  private setWorkTypeUnits(workTypeId: number, detailsData: IFwPlanPlanningWizardDetailsData): void {
    const workTypeUnits = this.workTypeUnitStore.getUnitsByWorktype(workTypeId);
    if (workTypeUnits.length === 1) {
      detailsData.Unit = workTypeUnits[0].Id;
    }
    this._responsiveData.WorkTypeUnits$.next(workTypeUnits);
    this._responsiveData.WorkTypeValueDisabled$.next(!workTypeUnits || workTypeUnits.length === 0);
    this._responsiveData.WorkTypeUnitsDisabled$.next(!workTypeUnits || workTypeUnits.length < 2);
  }

  /**
   * Sets available work types based on category
   * @param workTypeCategory - Selected work type category
   * @private
   */
  private setWorkTypes(workTypeCategory: number): void {
    const workTypes = this.getValidWorkTypes(workTypeCategory);
    this._responsiveData.WorkTypes$.next(workTypes);
  }

  /**
   * Sets available work type categories
   * @private
   */
  private setWorkTypeCategories(): void {
    const workTypeCategories = this.generateWorkTypeCategories();
    this._responsiveData.WorkTypeCategories$.next(workTypeCategories);
  }

  /**
   * Generates a list of unique work type categories
   * @private
   */
  private generateWorkTypeCategories(): WorkTypeCategory[] {
    return this.workTypeStore.WorkTypes.map(x => ({CategoryId: x.Category, CategoryText: x.CategoryText}))
      .filter((item, index, array) =>
        array.findIndex(obj => obj.CategoryId === item.CategoryId) === index
      );
  }

  /**
   * Gets valid work types for a category
   * @param categoryId - Category ID to filter by
   * @private
   */
  private getValidWorkTypes(categoryId: number | undefined): IWorktypes[] {
    return this.workTypeStore.WorkTypes
      .filter(w => !!w.Category)
      .filter(w => w.Category === categoryId);
  }
}
